import {ControlledDatePicker, ControlledTextField} from 'src/components/forms'
import {MessagesComponentProps} from '../../Messages'
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  ListItemIcon,
  MenuItem,
  Pagination,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {MensajesRequestFields} from '../../api/schemas'
import {MessageCardListDesktop} from '..'
import {EntityPaginationFields, EntityWithIdAndDescriptionFields} from 'src/types'
import {useSistemasPrincipales} from '../../api/useSistemasPrincipales'
import {EtapaEstado, EtapaEstadosFields} from 'src/types/workflow/cache'
import {useSistemas} from '../../api'
import CustomChip from 'src/@core/components/mui/chip'
import {ControlledAutocomplete} from 'src/components/forms/ControlledAutocomplete'
import {HttpCacheWorkflow} from 'src/http/workflow/httpCacheWorkflow'
import {hexToRGBA} from 'src/@core/utils/hex-to-rgba'
import {Icon} from '@iconify/react'
import React, {useState} from 'react'
import {userStorage} from '../../../../utils/localStorage'
import {NuevoMensaje} from '../NuevoMensaje'
import {CustomChipProps} from '../../../../@core/components/mui/chip/types'
import {inputMinStyles} from '../../../../components/forms/styles'
import FilterListIcon from '@mui/icons-material/FilterList'
import CloseIcon from '@mui/icons-material/Close'
import TuneIcon from '@mui/icons-material/Tune'

const formatDate = (date: any): string => {
    if (!date) return ''
    try {
        return new Date(date).toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit', year: 'numeric'})
    } catch {
        return ''
    }
}

// handleSearch es recibido pero el padre lo dispara vía useEffect sobre form.watch
export const MessagesDesktop = ({query, form}: MessagesComponentProps) => {
    const [drawerOpen, setDrawerOpen] = useState(false)
    const {sistemasPrincipal} = useSistemasPrincipales()
    const {sistemas} = useSistemas()

    const watchIsResponsible = form.watch(MensajesRequestFields.IsResponsible)
    const watchPaises = form.watch(MensajesRequestFields.CodsSistemaPrincipal) || []
    const watchWorkflows = form.watch(MensajesRequestFields.CodTipoMensaje) || []
    const watchEstados = form.watch(MensajesRequestFields.Estados) || []
    const watchId = form.watch(MensajesRequestFields.IdMensaje)
    const watchReferencia = form.watch(MensajesRequestFields.Referencia)
    const watchDesde = form.watch(MensajesRequestFields.FechaDesde)
    const watchHasta = form.watch(MensajesRequestFields.FechaHasta)

    const onChangeToggleResponsible = (_: React.MouseEvent<HTMLElement>, value: boolean) =>
        form.setValue(MensajesRequestFields.IsResponsible, value)

    const isDefaultEstados = watchEstados.length === 1 && watchEstados[0] === 1

    const activeFilterCount = [
        watchPaises.length > 0,
        watchWorkflows.length > 0,
        !isDefaultEstados,
        !watchIsResponsible,
        !!watchId,
        !!watchReferencia,
        !!watchDesde,
        !!watchHasta,
    ].filter(Boolean).length

    const handleClearFilters = () => {
        form.setValue(MensajesRequestFields.CodsSistemaPrincipal, [])
        form.setValue(MensajesRequestFields.CodTipoMensaje, [])
        form.setValue(MensajesRequestFields.Estados, [1])
        form.setValue(MensajesRequestFields.IsResponsible, true)
        form.setValue(MensajesRequestFields.IdMensaje, undefined)
        form.setValue(MensajesRequestFields.Referencia, undefined)
        form.setValue(MensajesRequestFields.FechaDesde, null)
        form.setValue(MensajesRequestFields.FechaHasta, null)
    }

    return (
        <>
            {/* Drawer de filtros */}
            <Drawer
                anchor='right'
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{sx: {width: 340, p: 3, display: 'flex', flexDirection: 'column'}}}
            >
                <Stack spacing={3} height='100%'>
                    <Stack direction='row' alignItems='center' justifyContent='space-between'>
                        <Stack direction='row' alignItems='center' gap={1}>
                            <TuneIcon color='primary' />
                            <Typography variant='h6' fontWeight={700}>
                                Filtros
                            </Typography>
                        </Stack>
                        <IconButton onClick={() => setDrawerOpen(false)} size='small'>
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    <Divider />

                    <Stack spacing={2.5} flex={1} overflow='auto'>
                        <ToggleButtonGroup
                            size='small'
                            exclusive
                            color='primary'
                            value={watchIsResponsible}
                            onChange={onChangeToggleResponsible}
                            sx={{width: '100%'}}
                        >
                            <ToggleButton value={true} sx={{width: '100%', ...inputMinStyles}}>
                                <Icon icon='material-symbols-light:star' style={{marginRight: 4}} />
                                Mis Pendientes
                            </ToggleButton>
                            <ToggleButton value={false} sx={{width: '100%', ...inputMinStyles}}>
                                Todos
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <ControlledAutocomplete
                            label='País'
                            name={MensajesRequestFields.CodsSistemaPrincipal}
                            control={form.control}
                            multiple
                            valueField='id'
                            loadOptions={() => Promise.resolve(sistemasPrincipal || [])}
                            min
                        />

                        <ControlledAutocomplete
                            label='Workflow'
                            name={MensajesRequestFields.CodTipoMensaje}
                            control={form.control}
                            multiple
                            valueField='id'
                            loadOptions={() => Promise.resolve(sistemas || [])}
                            min
                        />

                        <ControlledAutocomplete
                            label='Estado'
                            name={MensajesRequestFields.Estados}
                            control={form.control}
                            multiple
                            valueField='id'
                            loadOptions={HttpCacheWorkflow.getEtapaEstados}
                            min
                            TagComponent={(value: EtapaEstado) => {
                                return (props: any) => (
                                    <ChipTag
                                        label={value[EntityWithIdAndDescriptionFields.Description]}
                                        color={value[EtapaEstadosFields.HexColor]}
                                        {...props}
                                    />
                                )
                            }}
                            renderOption={(renderProps, value: EtapaEstado, state) => (
                                <MenuItem key={state.index} selected={state.selected} value={value.id} {...renderProps}>
                                    <ListItemIcon>
                                        <Icon icon='mdi:circle' color={value[EtapaEstadosFields.HexColor]} fontSize='1rem' />
                                    </ListItemIcon>
                                    {value.descripcion}
                                </MenuItem>
                            )}
                        />

                        <ControlledTextField
                            label='Id'
                            control={form.control}
                            fullWidth
                            name={MensajesRequestFields.IdMensaje}
                            numeric
                            min
                        />

                        <ControlledTextField
                            label='Referencia'
                            control={form.control}
                            fullWidth
                            name={MensajesRequestFields.Referencia}
                            size='small'
                            min
                        />

                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                                <ControlledDatePicker
                                    label='Desde'
                                    control={form.control}
                                    fullWidth
                                    name={MensajesRequestFields.FechaDesde}
                                    clearable
                                    min
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <ControlledDatePicker
                                    label='Hasta'
                                    control={form.control}
                                    fullWidth
                                    name={MensajesRequestFields.FechaHasta}
                                    clearable
                                    min
                                />
                            </Grid>
                        </Grid>
                    </Stack>

                    <Divider />

                    <Button variant='outlined' color='inherit' fullWidth onClick={handleClearFilters}>
                        Limpiar filtros
                    </Button>
                </Stack>
            </Drawer>

            {/* Contenido principal */}
            <Stack spacing={3}>
                {/* Barra principal */}
                <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2} flexWrap='wrap'>
                    {/* Izquierda: Nuevo + chips de filtros activos */}
                    <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap' flex={1} minWidth={0}>
                        {userStorage.canCreateNewMessage() && <NuevoMensaje />}

                        <Chip
                            size='small'
                            icon={watchIsResponsible ? <Icon icon='material-symbols-light:star' /> : undefined}
                            label={watchIsResponsible ? 'Mis Pendientes' : 'Todos'}
                            color={watchIsResponsible ? 'primary' : 'default'}
                            variant='outlined'
                            onClick={() => form.setValue(MensajesRequestFields.IsResponsible, !watchIsResponsible)}
                        />

                        {watchPaises.length > 0 && (
                            <Chip
                                size='small'
                                label={`${watchPaises.length} ${watchPaises.length === 1 ? 'País' : 'Países'}`}
                                color='info'
                                variant='outlined'
                                onDelete={() => form.setValue(MensajesRequestFields.CodsSistemaPrincipal, [])}
                            />
                        )}

                        {watchWorkflows.length > 0 && (
                            <Chip
                                size='small'
                                label={`${watchWorkflows.length} ${watchWorkflows.length === 1 ? 'Workflow' : 'Workflows'}`}
                                color='info'
                                variant='outlined'
                                onDelete={() => form.setValue(MensajesRequestFields.CodTipoMensaje, [])}
                            />
                        )}

                        {!isDefaultEstados && watchEstados.length > 0 && (
                            <Chip
                                size='small'
                                label={`${watchEstados.length} ${watchEstados.length === 1 ? 'Estado' : 'Estados'}`}
                                color='secondary'
                                variant='outlined'
                                onDelete={() => form.setValue(MensajesRequestFields.Estados, [1])}
                            />
                        )}

                        {watchId && (
                            <Chip
                                size='small'
                                label={`Id: ${watchId}`}
                                variant='outlined'
                                onDelete={() => form.setValue(MensajesRequestFields.IdMensaje, undefined)}
                            />
                        )}

                        {watchReferencia && (
                            <Chip
                                size='small'
                                label={`Ref: ${watchReferencia}`}
                                variant='outlined'
                                onDelete={() => form.setValue(MensajesRequestFields.Referencia, undefined)}
                            />
                        )}

                        {watchDesde && (
                            <Chip
                                size='small'
                                label={`Desde: ${formatDate(watchDesde)}`}
                                variant='outlined'
                                onDelete={() => form.setValue(MensajesRequestFields.FechaDesde, null)}
                            />
                        )}

                        {watchHasta && (
                            <Chip
                                size='small'
                                label={`Hasta: ${formatDate(watchHasta)}`}
                                variant='outlined'
                                onDelete={() => form.setValue(MensajesRequestFields.FechaHasta, null)}
                            />
                        )}
                    </Stack>

                    {/* Derecha: botón Filtros */}
                    <Badge badgeContent={activeFilterCount} color='primary'>
                        <Button
                            variant='outlined'
                            startIcon={<FilterListIcon />}
                            onClick={() => setDrawerOpen(true)}
                            sx={{borderRadius: '10px', whiteSpace: 'nowrap'}}
                        >
                            Filtros
                        </Button>
                    </Badge>
                </Stack>

                {/* Resultados */}
                {query.status === 'loading' && (
                    <Box sx={{display: 'flex', justifyContent: 'center'}}>
                        <CircularProgress disableShrink sx={{mt: 6}} />
                    </Box>
                )}
                {query.status === 'error' && (
                    <Alert severity='error'>Se produjo un error al cargar los mensajes.</Alert>
                )}
                {query.status === 'success' &&
                    (!query.entities.length ? (
                        <Alert severity='info'>No se encontraron mensajes para esta selección.</Alert>
                    ) : (
                        <MessageCardListDesktop messages={query.entities} />
                    ))}
                {query.status === 'success' && !!query.entities.length && (
                    <Stack alignItems='center'>
                        <Pagination
                            count={query.paginacion[EntityPaginationFields.CantPages]}
                            page={query.paginacion[EntityPaginationFields.ActualPage]}
                            onChange={(_, page) => query.onChange(page)}
                        />
                    </Stack>
                )}
            </Stack>
        </>
    )
}

export const ChipTag = ({label, color, ...rest}: CustomChipProps) => {
    return (
        <CustomChip
            label={label}
            skin='light'
            size='small'
            {...rest}
            sx={{
                color: color,
                backgroundColor: hexToRGBA(color || 'primary.main', 0.12),
                ...rest?.sx,
            }}
        />
    )
}
